import { Link2, Loader2, LockKeyhole, MonitorSmartphone, Network } from "lucide-react"
import { PairComputerForm } from "../components/computers/PairComputerForm"
import { useComputers } from "../computers/context"

export function ConnectComputerPage({
    inviteStatus,
    accessIssue,
}: {
    inviteStatus: { state: "connecting" | "error"; error?: string } | null
    accessIssue?: string | null
}) {
    const computers = useComputers()
    if (inviteStatus?.state === "connecting") {
        return (
            <div className="code-theme code-theme-black flex min-h-screen w-screen items-center justify-center bg-base-100 p-5 text-base-content">
                <div className="flex max-w-sm flex-col items-center text-center">
                    <div className="mb-6 flex h-14 w-14 items-center justify-center border border-primary/40 bg-primary/10">
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    </div>
                    <h1 className="text-xl font-semibold tracking-tight">Connecting securely…</h1>
                    <p className="mt-2 text-sm leading-6 text-muted">Confirming the computer and creating this browser&apos;s revocable device token.</p>
                </div>
            </div>
        )
    }
    return (
        <div className="code-theme code-theme-black flex min-h-screen w-screen items-center justify-center bg-base-100 p-5 text-base-content">
            <div className="w-full max-w-md">
                <div className="mb-7 flex h-12 w-12 items-center justify-center border border-border bg-base-200">
                    <MonitorSmartphone className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-2xl font-semibold tracking-tight">WildLark from anywhere</h1>
                <p className="mt-2 text-sm leading-6 text-muted">
                    On one of your computers, open Settings → Sharing, turn on Reachable from anywhere, and choose Add a device. That one-time link securely
                    adds the computer here.
                </p>
                {inviteStatus?.state === "error" && (
                    <div role="alert" className="mt-5 border border-error/40 bg-error/10 p-4 text-sm text-error">
                        <div className="font-medium">This pairing link could not be used</div>
                        <div className="mt-1 text-xs leading-5">
                            {inviteStatus.error ?? "Generate a fresh link from the WildLark desktop app and try again."}
                        </div>
                    </div>
                )}
                {accessIssue && (
                    <div role="alert" className="mt-5 border border-warning/40 bg-warning/10 p-4 text-sm text-base-content">
                        <div className="font-medium">This browser is no longer paired</div>
                        <div className="mt-1 text-xs leading-5 text-muted">{accessIssue}</div>
                    </div>
                )}
                <div className="my-6 border border-border bg-base-200/50 p-4">
                    <div className="flex gap-3 text-xs leading-5 text-muted">
                        <LockKeyhole className="mt-0.5 h-4 w-4 flex-none text-success" />
                        <span>
                            The secret stays inside the link and is removed from the address bar immediately. Your work and provider credentials stay on the
                            computer.
                        </span>
                    </div>
                </div>
                <div className="border border-border p-4">
                    <div className="flex gap-3">
                        <Link2 className="mt-0.5 h-4 w-4 flex-none text-primary" />
                        <div>
                            <div className="text-sm font-medium text-base-content">On your computer</div>
                            <div className="mt-1 text-xs leading-5 text-muted">
                                Open Computers → Pair device, then scan the QR code or send yourself the link.
                            </div>
                        </div>
                    </div>
                </div>
                <details className="mt-5">
                    <summary className="cursor-pointer text-xs font-medium text-muted hover:text-base-content">Advanced: paste a pairing link</summary>
                    <div className="mt-4">
                        <PairComputerForm busy={computers.busy} onSubmit={computers.pairAndAdd} />
                    </div>
                </details>
                <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted">
                    <Network className="h-3.5 w-3.5" />
                    Private network connection
                </div>
            </div>
        </div>
    )
}
